import axios from "axios";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toastr from "../../../../utils/toast";

const API = process.env.REACT_APP_API_BASE_URL;

function Reimbursement({ data, onClose }) {
    const { setGlobalLoading, token } = useOutletContext();

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(true);

    // Approval
    const [approvalAction, setApprovalAction] = useState("");
    const [approvalComments, setApprovalComments] = useState("");
    const [approvalSubmitting, setApprovalSubmitting] = useState(false);

    const authHeaders = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    // =========================================================
    // GET REIMBURSEMENT DETAILS
    // =========================================================
    const getReimbursementDetail = async () => {
        if (!data?.id) {
            toastr.error("Reimbursement ID not found.");
            return;
        }

        setDetailLoading(true);

        try {
            const response = await axios.get(
                `${API}/reimbursements/${data.id}/detail`,
                authHeaders
            );

            setDetail(response?.data?.data || null);
        } catch (error) {
            console.error(
                "[getReimbursementDetail] ERROR:",
                error
            );

            toastr.error(
                error?.response?.data?.message ||
                    "Failed to load reimbursement details."
            );
        } finally {
            setDetailLoading(false);
        }
    };

    useEffect(() => {
        getReimbursementDetail();
    }, [data?.id]);

    // =========================================================
    // SUBJECT
    // =========================================================
    const subjectOf = (record) => {
        if (record?.subject) {
            return record.subject;
        }

        if (record?.travel_from || record?.travel_to) {
            const location = record.travel_location
                ? ` (${record.travel_location})`
                : "";

            return `${record.travel_from || ""} → ${
                record.travel_to || ""
            }${location}`;
        }

        return "—";
    };

    // =========================================================
    // VIEW REIMBURSEMENT FILE
    // =========================================================
    const viewFile = async (savename) => {
        if (!savename) {
            return;
        }

        try {
            const response = await fetch(
                `${API}/files/mediaReimbursement/${savename}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Unable to open file.");
            }

            const blob = await response.blob();

            const fileUrl = window.URL.createObjectURL(blob);

            window.open(fileUrl, "_blank");

            setTimeout(() => {
                window.URL.revokeObjectURL(fileUrl);
            }, 1000);
        } catch (error) {
            console.error("[viewFile] ERROR:", error);

            toastr.error("Failed to open file.");
        }
    };

    // =========================================================
    // TOTAL BILL AMOUNT
    // =========================================================
    const billsTotal =
        detail?.files?.reduce(
            (total, file) =>
                total + (parseFloat(file.entity_amount) || 0),
            0
        ) || 0;

    // =========================================================
    // SUBMIT APPROVAL / REJECTION
    // =========================================================
    const submitApproval = async () => {
        if (!detail?.record?.id) {
            toastr.error("Reimbursement details not found.");
            return;
        }

        if (!approvalAction) {
            toastr.warning("Please select Approve or Reject.");
            return;
        }

        if (!approvalComments.trim()) {
            toastr.warning("Please enter comments.");
            return;
        }

        setApprovalSubmitting(true);
        setGlobalLoading(true);

        try {
            await axios.put(
                `${API}/reimbursements/${detail.record.id}/approval`,
                {
                    action: approvalAction,
                    comments: approvalComments.trim(),
                },
                authHeaders
            );

            toastr.success(
                approvalAction === "yes"
                    ? "Reimbursement approved successfully."
                    : "Reimbursement rejected successfully."
            );

            setApprovalAction("");
            setApprovalComments("");

            /*
             * Close this component and tell Notifications.jsx
             * to refresh the reimbursement list.
             */
            onClose(true);
        } catch (error) {
            console.error(
                "[submitApproval] ERROR:",
                error
            );

            toastr.error(
                error?.response?.data?.message ||
                    "Failed to process reimbursement."
            );
        } finally {
            setApprovalSubmitting(false);
            setGlobalLoading(false);
        }
    };

    // =========================================================
    // MAIN MODAL
    // =========================================================
    return (
        <>
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{
                    background: "rgba(0, 0, 0, 0.5)",
                }}
            >
                <div className="modal-dialog modal-xl modal-dialog-scrollable">
                    <div className="modal-content">

                        {/* =================================================
                            HEADER
                        ================================================== */}
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Reimbursement Details
                            </h5>

                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => onClose(false)}
                                disabled={approvalSubmitting}
                            />
                        </div>

                        {/* =================================================
                            BODY
                        ================================================== */}
                        <div className="modal-body">

                            {detailLoading || !detail ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" />

                                    <div className="mt-2 text-muted">
                                        Loading reimbursement details...
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* =================================================
                                        REIMBURSEMENT DETAILS
                                    ================================================== */}
                                    <div className="card border mb-1">
                                        <div className="card-body">
                                            <div className="row">
                                                {/* LEFT COLUMN */}
                                                <div className="col-md-6">
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Name:</div>
                                                        <div className="col-sm-8">{detail.record.employee_name || "—"}</div>
                                                    </div>
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Subject:</div>
                                                        <div className="col-sm-8">{subjectOf(detail.record)}</div>
                                                    </div>
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Submit Date:</div>
                                                        <div className="col-sm-8">
                                                            {detail.record.created_date ? new Date(detail.record.created_date).toLocaleDateString("en-GB") : "—"}
                                                        </div>
                                                    </div>
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Status:</div>
                                                        <div className="col-sm-8">
                                                            <span className="text-capitalize">{detail.record.status || "pending"}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* RIGHT COLUMN */}
                                                <div className="col-md-6">
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Expense Type:</div>
                                                        <div className="col-sm-8">{detail.record.expense_name || "—"}</div>
                                                    </div>
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Authorised By:</div>
                                                        <div className="col-sm-8">{detail.record.authorised_by_name || "—"}</div>
                                                    </div>
                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Payment To:</div>
                                                        <div className="col-sm-8">{detail.record.payment_to || "—"}</div>
                                                    </div>

                                                    <div className="row mb-1">
                                                        <div className="col-sm-4 fw-semibold">Bill Amount:</div>
                                                        <div className="col-sm-8">{detail.record.entity_currency_name}{" "}{detail.record.final_amount || 0}</div>
                                                    </div>
                                                    {detail.record.savename && (
                                                        <div className="row mb-1">
                                                            <div className="col-sm-4 fw-semibold">Other File:</div>
                                                            <div className="col-sm-8">
                                                                <button type="button" className="btn btn-sm btn-outline-info" onClick={() => viewFile(detail.record.savename)}>
                                                                    <i className="ti ti-paperclip me-1" />
                                                                    View
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* =================================================
                                        BILLS
                                    ================================================== */}
                                    <div className="card border">
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-bordered align-middle mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Bill Date</th>
                                                            <th>Type</th>
                                                            <th>Remarks</th>
                                                            <th>Amount</th>
                                                            <th>Currency</th>
                                                            <th>Conv. Rate</th>
                                                            <th>Conv. Amount</th>
                                                            <th>File</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {detail.files?.map(
                                                            (file) => (
                                                                <tr key={file.id}>
                                                                    <td>{file.bill_date ? new Date(file.bill_date).toLocaleDateString("en-GB") : "—"}</td>
                                                                    <td>{file.reimbursement_type_name || "—"}</td>
                                                                    <td>{file.remarks || "—"}</td>
                                                                    <td>{file.amount}</td>
                                                                    <td>{file.currency_name || file.currency_symbol || "—"}</td>
                                                                    <td>{file.conversion_rate}</td>
                                                                    <td>{file.entity_amount}</td>
                                                                    <td>
                                                                        {file.savename ? (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-info"
                                                                                title="View Bill"
                                                                                onClick={() =>
                                                                                    viewFile(
                                                                                        file.savename
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i className="ti ti-paperclip" />
                                                                            </button>
                                                                        ) : (
                                                                            "—"
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            )
                                                        )}

                                                        {(!detail.files ||
                                                            detail.files
                                                                .length ===
                                                                0) && (
                                                            <tr>
                                                                <td
                                                                    colSpan="8"
                                                                    className="text-center text-muted py-3"
                                                                >
                                                                    No bills
                                                                    found.
                                                                </td>
                                                            </tr>
                                                        )}

                                                    </tbody>

                                                    {detail.files?.length >
                                                        0 && (
                                                        <tfoot>
                                                            <tr>
                                                                <th
                                                                    colSpan="6"
                                                                    className="text-end"
                                                                >
                                                                    Total
                                                                    Conv.
                                                                    Amount
                                                                </th>

                                                                <th>
                                                                    {billsTotal.toLocaleString(
                                                                        "en-IN"
                                                                    )}
                                                                </th>

                                                                <th />
                                                            </tr>
                                                        </tfoot>
                                                    )}

                                                </table>

                                            </div>
                                        </div>
                                    </div>

                                    {/* =================================================
                                        APPROVAL
                                    ================================================== */}
                                    <div className="card border mt-3">

                                        <div className="card-header">
                                            <h6 className="mb-0">
                                                Approval
                                            </h6>
                                        </div>

                                        <div className="card-body">

                                            <div className="row align-items-end">

                                                {/* ACTION */}
                                                <div className="col-md-4">
                                                    <label className="form-label"> Action{" "} <span className="text-danger"> * </span></label>
                                                    <select className="form-select"
                                                        value={approvalAction}
                                                        onChange={(e) =>
                                                            setApprovalAction(
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={
                                                            approvalSubmitting
                                                        }
                                                    >
                                                        <option value="">
                                                            Select Action
                                                        </option>

                                                        <option value="yes">
                                                            Yes
                                                        </option>

                                                        <option value="no">
                                                            No
                                                        </option>
                                                    </select>
                                                </div>

                                                {/* COMMENTS */}
                                                <div className="col-md-5">
                                                    <label className="form-label">
                                                        Comments{" "}
                                                        <span className="text-danger">
                                                            *
                                                        </span>
                                                    </label>

                                                    <textarea
                                                        className="form-control"
                                                        rows="1"
                                                        placeholder="Enter comments..."
                                                        value={
                                                            approvalComments
                                                        }
                                                        onChange={(e) =>
                                                            setApprovalComments(
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={
                                                            approvalSubmitting
                                                        }
                                                    />
                                                </div>

                                                {/* SUBMIT */}
                                                <div className="col-md-3 text-end">
                                                    <button
                                                        type="button"
                                                        className={`btn ${approvalAction === "no" ? "btn-danger" : "btn-primary"}`}
                                                        onClick={submitApproval}
                                                        disabled={
                                                            approvalSubmitting ||
                                                            !approvalAction ||
                                                            !approvalComments.trim()
                                                        }
                                                    >
                                                        {approvalSubmitting ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-1"
                                                                    role="status"
                                                                />

                                                                Processing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i
                                                                    className={`ti ${
                                                                        approvalAction ===
                                                                        "no"
                                                                            ? "ti-x"
                                                                            : "ti-check"
                                                                    } me-1`}
                                                                />

                                                                Submit
                                                            </>
                                                        )}
                                                    </button>
                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* =================================================
                            FOOTER
                        ================================================== */}
                        <div className="modal-footer">

                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => onClose(false)}
                                disabled={approvalSubmitting}
                            >
                                Close
                            </button>

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default Reimbursement;